import * as React from 'react';
import { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';

export interface IEditTimeDialogData {
    tmId: number;
    type: string;
    action: string;
    name: string;
    disp1: string;
    disp2: string;
    disp3: string;
    docNo?: number;
    dayRate?: number;
    docDate?: string;
}

interface IEditTimeDialogProps {
    dialogName: string;
    hideDialog: () => void;
    data: IEditTimeDialogData;
    onSave: (tmId: number, type: string, docNo: number | null, dayRate: number | null, docDate: string | null) => void;
}

const toDateInputValue = (value: string | undefined) => (value ? value.slice(0, 10) : '');

// A blank field has to stay blank all the way to the server. Number('') is 0, and the server's
// guards - which mirror the form's own alerts - only reject null, so a blank Pay/Bill Rate would
// arrive as a valid-looking 0 and overwrite the real rate on an otherwise complete edit.
const toNumberOrNull = (value: string) => {
    const trimmed = (value ?? '').trim();
    if (trimmed === '') {
        return null;
    }
    const parsed = Number(trimmed);
    return isNaN(parsed) ? null : parsed;
};

// Mirrors te_nonrecon.fmt's EDIT_TIME canvas/window (block EDIT), reached by double-clicking a
// "Project Information" row (WHEN-MOUSE-DOUBLECLICK, oid 285). ACTION's yellow background and
// NAME's gray bar are the real screen's own item background colors, confirmed from a screenshot
// of the actual Oracle Forms canvas rather than guessed. Dialog fully unmounts on close
// (Dialog's own CSSTransition unmountOnExit), so plain useState initialized from props is enough -
// no stale state to reset between rows.
const EditTimeDialog = (props: IEditTimeDialogProps) => {
    const data = props.data;
    const [docNo, setDocNo] = useState<string>(data?.docNo !== undefined && data?.docNo !== null ? String(data.docNo) : '');
    const [dayRate, setDayRate] = useState<string>(data?.dayRate !== undefined && data?.dayRate !== null ? String(data.dayRate) : '');
    const [docDate, setDocDate] = useState<string>(toDateInputValue(data?.docDate));

    if (!data) {
        return null;
    }

    const handleSave = () => {
        props.onSave(data.tmId, data.type, toNumberOrNull(docNo), toNumberOrNull(dayRate), docDate || null);
    };

    const renderRow = (label: string, value: string, onChange: (value: string) => void, type: 'number' | 'date') => (
        <div className="form-row" style={{ alignItems: 'center', margin: 0, padding: '.25rem 0' }}>
            <label style={{ width: '9rem' }}>{label}</label>
            <input
                type={type}
                className="field"
                style={{ flex: 1 }}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );

    return (
        <Dialog title="Edit Time Entry" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '28rem' }}>
            <div className="metallic-dialog-body" style={{ padding: 0 }}>
                <div style={{ backgroundColor: '#FFFF00', textAlign: 'center', fontWeight: 'bold', padding: '.5rem' }}>{data.action}</div>
                <div style={{ backgroundColor: '#D3D3D3', textAlign: 'center', padding: '.5rem' }}>{data.name}</div>
                <div style={{ padding: '1rem 1.5rem' }}>
                    {renderRow(data.disp1, docNo, setDocNo, 'number')}
                    {renderRow(data.disp2, dayRate, setDayRate, 'number')}
                    {renderRow(data.disp3, docDate, setDocDate, 'date')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', padding: '0 1rem 1rem' }}>
                    <Button className="blue-hover-button" style={{ width: '8rem', height: '2.5rem' }} text="OK" onClick={handleSave} />
                    <Button className="blue-hover-button" style={{ width: '8rem', height: '2.5rem' }} text="Cancel" onClick={() => props.hideDialog()} />
                </div>
            </div>
        </Dialog>
    );
};

export default EditTimeDialog;



// WEBPACK FOOTER //
// ./src/components/project-billing/dialogs/EditTimeDialog.tsx